import { Link, useRouterState } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { TIGHT, useMedia } from '../lib/media';
import { useMe } from '../lib/queries';
import { settingsOf } from '../lib/settings';
import { dismissReady, useReadyAnswer } from '../state/answers';
import { useChrome } from '../state/chrome';
import { Icon } from '../ui';
import './askdock.css';

// The list itself comes with Explore's code, when the button is first pressed.
const Earlier = lazy(() => import('./explore/Earlier').then((m) => ({ default: m.Earlier })));

/** A book's reader, and the book it is open on. */
const READER = /^\/books\/([^/]+)\/read\//;
/** Where the panel is a sheet along the foot of the window (app.css, NARROW WINDOWS). */
const PHONE = '(max-width: 760px)';

/**
 * The round button in the bottom corner of the reader, and of no other
 * screen: it opens the reader's past conversations, the ones begun on this
 * book first, over a field that asks about the book in the reader's Ask
 * panel. It stays while the bar comes and goes, in the paper's colours, and
 * is away only while something else has its corner. A phone's page has
 * no room for it (its foot margin is one line of small type): there the
 * spark in the reader's bar opens the same list, and the corner is empty.
 * Beside it, for a moment, the notice that an answer asked for earlier is
 * ready, in the corner by itself on a phone; on the app's own pages that
 * notice is a toast (Shell).
 */
export function AskDock() {
  const path = useRouterState({ select: (r) => r.location.pathname });
  const readerPanel = useChrome((s) => s.readerPanel);
  const book = READER.exec(path)?.[1];
  const ready = useReadyAnswer(!!book);
  const paper = settingsOf(useMe().data).reader.paper;
  const phone = useMedia(PHONE);
  const tight = useMedia(TIGHT);
  const open = useChrome((s) => s.past);
  const setOpen = useChrome((s) => s.openPast);
  // Open on the page it was opened on: moving to another page closes it.
  useEffect(() => setOpen(false), [path, setOpen]);
  // What is being written in the field, kept while the panel closes and opens again.
  const [draft, setDraft] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      // The bar's spark, on a phone, opens and closes it itself.
      const t = e.target as Element;
      if (!box.current?.contains(t) && !t.closest?.('[data-past]')) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      setOpen(false);
      (button.current ?? document.querySelector<HTMLElement>('[data-past]'))?.focus();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);

  // A review session has no corner button, and nothing is said over it.
  if (!book) return null;

  // Always there on the page, the bar up or away; Aa and Contents have the corner while they are open.
  const shown = !tight && (open || readerPanel === 'none');

  return (
    <div ref={box} className="askdock on-paper" data-paper={paper}>
      {ready ? (
        <ReadyNotice conversationId={ready.id} failed={ready.failed} onDismiss={dismissReady} />
      ) : null}
      {shown ? <DockButton ref={button} open={open} onClick={() => setOpen(!open)} /> : null}
      {open ? (
        <Suspense fallback={null}>
          <Earlier
            here={{ label: 'Earlier on this book', begun: { book_id: book } }}
            place={phone ? 'sheet' : 'side'}
            placeholder="Ask about this book"
            input={draft}
            onInput={setDraft}
            onAsk={(question) => {
              // Asked in the reader's own Ask panel, which opens on it in place of this.
              setDraft('');
              setOpen(false);
              useChrome.getState().askInReader(question);
            }}
            onClose={() => setOpen(false)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

/** The button as drawn: the gallery renders this. */
export function DockButton({
  open,
  onClick,
  ref,
}: {
  open: boolean;
  onClick: () => void;
  ref?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={ref}
      type="button"
      className="askdock-btn"
      aria-label="Past conversations"
      title="Past conversations"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={onClick}
    >
      <Icon name="spark" size={20} />
    </button>
  );
}

/**
 * An answer asked for earlier ended while the reader was elsewhere: said
 * with the way to it. Beside the button in the reader; on the app's pages,
 * which have no corner button, as the app's toast.
 */
export function ReadyNotice({
  conversationId,
  failed,
  toast,
  onDismiss,
}: {
  conversationId: string;
  /** It ended without an answer: said plainly, with the same way to it. */
  failed?: boolean;
  /** Drawn as the app's toast, not beside the corner button. */
  toast?: boolean;
  onDismiss: () => void;
}) {
  return (
    <div className={toast ? 'toast toast-ready' : 'askdock-ready'} role="status">
      <span>{failed ? 'An answer didn’t finish' : 'Your answer is ready'}</span>
      <Link
        to="/explore/c/$conversationId"
        params={{ conversationId }}
        className={toast ? 'toast-act' : 'btn btn-secondary btn-sm'}
        onClick={onDismiss}
      >
        Open
      </Link>
      <button
        type="button"
        className={toast ? 'toast-x' : 'askdock-x'}
        aria-label="Dismiss"
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}
